import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import TwitterText from 'twitter-text';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

import { yupResolver } from '@hookform/resolvers';
import { RequiredString } from 'helpers/yupHelper';
import { facebookURLs } from 'Utils/FacebookURLs';
import { selectUser } from 'redux/slices/userSlice';
import { awsUploadVideoHelper } from 'helpers/awsVideoHelper';
import {
  awsUploadFileHelper,
  setFileNameHelper
} from 'helpers/awsCampaignFileHelper';

import { getUrlS3 } from 'constants/setup.constants';

import { ErrorAlert } from 'components/Alert';
import { SuccessAlert } from 'components/Alert';
import CreateSuggestedPostForm from './CreateSuggestedPostForm';
import { createSuggestedPost } from 'services/rest/SuggestedPost/createSuggestedPost';
import { getSuggestedPostDetail } from 'services/rest/SuggestedPost/getSuggestedPostDetail';
import { updateSuggestedPost } from 'services/rest/SuggestedPost/updateSuggestedPost';

const CreateSuggestedPostPage: React.FC = () => {
  const no_characters = /^(?!.*[!@#$%^&*()\-_+={}[\]|\\;:'"ª|!· º\\,<.>/?]{5}).+/;
  const no_mentions = /^(?!.*(?:\B@|https?:\/\/|www\.|\b[\w-]+\.(?:com|net|org|io|co|me|tv|app|ly|gg)\b))[\s\S]+$/i;
  const { t } = useTranslation(['suggested_post', 'validations']);
  const userSchema = yup.object().shape({
    name: RequiredString().max(
      150,
      t('validations:length_error', { max: 150 })
    ),
    description: yup
      .string()
      .max(1800, t('validations:length_error', { max: 1800 }))
  });

  const { register, control, handleSubmit, setValue, errors, watch } = useForm({
    resolver: yupResolver(userSchema)
  });

  const [backendError, setBackendError] = useState<string | null>(null);
  const [errorSocialNetwork, setErrorSocialNetwork] = useState<string>('');
  const [fileErrorInstagram, setFileErrorInstagram] = useState<string>('');
  const [fileErrorTwitter] = useState<string>('');
  const [fileErrorLinkedin, setFileErrorLinkedin] = useState<string>('');
  const [fileErrorWhatsapp, setFileErrorWhatsapp] = useState<string>('');
  const [fileErrorTiktok, setFileErrorTiktok] = useState<string>('');
  const [backendSuccess, setBackendSuccess] = useState<string | null>(null);
  // const [image, setImage] = useState<Array<any>>([]);
  const [imageTwitter, setImageTwitter] = useState<Array<any>>([]);
  const [imageLinkedin, setImageLinkedin] = useState<Array<any>>([]);
  const [imageInstagram, setImageInstagram] = useState<Array<any>>([]);
  const [imageWhatsapp, setImageWhatsapp] = useState<Array<any>>([]);
  const [imageTiktok, setImageTiktok] = useState<Array<any>>([]);
  const [type, setType] = useState({
    instagram: 'text',
    twitter: 'text',
    linkedin: 'text',
    whatsapp: 'text',
    tiktok: 'text'
  });
  const [loader, setLoader] = useState(false);
  const [uploadedTwitter, setUploadedTwitter] = useState<boolean | null>(null);
  const [uploadedLinkedin, setUploadedLinkedin] = useState<boolean | null>(
    null
  );
  const [uploadedInstagram, setUploadedInstagram] = useState<boolean | null>(
    null
  );
  const [uploadedTiktok, setUploadedTiktok] = useState<boolean | null>(null);
  const [uploadedWhatsapp, setUploadedWhatsapp] = useState<boolean | null>(
    null
  );
  const isDraft = useRef(false);
  const [localIsDraft, setLocalIsDraft] = useState(false);
  const history = useHistory();
  const { currentUser } = useSelector(selectUser);
  const [contentLimits] = useState({
    twitter: 280,
    linkedin: 1500,
    instagram: 700,
    whatsapp: 280,
    tiktok: 150
  });

  const [initialSocialNetworks, setInitialSocialNetworks] = useState({
    facebook: false,
    twitter: false,
    linkedin: false,
    instagram: false,
    tiktok: false,
    whatsapp: false
  });

  const [invalidLimit, setInvalidLimit] = useState({
    facebook: false,
    twitter: false,
    linkedin: false,
    instagram: false,
    tiktok: false,
    whatsapp: false
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const { id } = useParams<{ id: string }>();

  React.useEffect(() => {
    if (id) {
      getData();
    }
  }, [id]);

  const getData = async () => {
    setLoader(true);
    const { data } = await getSuggestedPostDetail(id, currentUser?.token);
    if (data) {
      const suggestedPost = data.data;
      setLocalIsDraft(suggestedPost.status === 'draft');
      isDraft.current = suggestedPost.status === 'draft';
      setValue('name', suggestedPost.name);

      setValue('description', suggestedPost.description);
      setValue('includesFacebook', !!suggestedPost.includesFacebook);
      setValue('includesTwitter', !!suggestedPost.includesTwitter);
      setValue('includesLinkedin', !!suggestedPost.includesLinkedin);
      setValue('includesInstagram', !!suggestedPost.includesInstagram);
      setValue('includesWhatsapp', !!suggestedPost.includesWhatsapp);
      setValue('includesTiktok', !!suggestedPost.includesTiktok);

      setInitialSocialNetworks({
        facebook: !!suggestedPost.includesFacebook,
        twitter: !!suggestedPost.includesTwitter,
        linkedin: !!suggestedPost.includesLinkedin,
        instagram: !!suggestedPost.includesInstagram,
        tiktok: !!suggestedPost.includesTiktok,
        whatsapp: !!suggestedPost.includesWhatsapp
      });

      setValue(
        'linkFacebook',
        suggestedPost?.contentPerSocialNetwork?.facebook?.link ||
          suggestedPost?.contentPerSocialNetwork?.facebook?.content
      );
      setValue(
        'contentTwitter',
        suggestedPost?.contentPerSocialNetwork?.twitter?.content
      );
      setValue(
        'contentLinkedin',
        suggestedPost?.contentPerSocialNetwork?.linkedin?.content
      );
      setValue(
        'contentInstagram',
        suggestedPost?.contentPerSocialNetwork?.instagram?.content
      );
      setValue(
        'contentWhatsapp',
        suggestedPost?.contentPerSocialNetwork?.whatsapp?.content
      );
      setValue(
        'contentTiktok',
        suggestedPost?.contentPerSocialNetwork?.tiktok?.content
      );

      setType({
        instagram:
          suggestedPost?.contentPerSocialNetwork?.instagram?.type || 'text',
        twitter:
          suggestedPost?.contentPerSocialNetwork?.twitter?.type || 'text',
        linkedin:
          suggestedPost?.contentPerSocialNetwork?.linkedin?.type || 'text',
        whatsapp:
          suggestedPost?.contentPerSocialNetwork?.whatsapp?.type || 'text',
        tiktok: suggestedPost?.contentPerSocialNetwork?.tiktok?.type || 'video'
      });

      if (
        suggestedPost?.contentPerSocialNetwork?.twitter?.picturesUrls?.length >
        0
      ) {
        setImageTwitter(
          suggestedPost.contentPerSocialNetwork.twitter.picturesUrls.map(
            (url: string) => ({ url })
          )
        );
      } else if (suggestedPost?.contentPerSocialNetwork?.twitter?.videoUrl) {
        setImageTwitter([
          { url: suggestedPost.contentPerSocialNetwork.twitter.videoUrl }
        ]);
      }
      if (
        suggestedPost?.contentPerSocialNetwork?.linkedin?.picturesUrls?.length >
        0
      ) {
        setImageLinkedin(
          suggestedPost.contentPerSocialNetwork.linkedin.picturesUrls.map(
            (url: string) => ({ url })
          )
        );
      } else if (suggestedPost?.contentPerSocialNetwork?.linkedin?.videoUrl) {
        setImageLinkedin([
          { url: suggestedPost.contentPerSocialNetwork.linkedin.videoUrl }
        ]);
      } else if (suggestedPost?.contentPerSocialNetwork?.linkedin?.pdfUrl) {
        setImageLinkedin([
          { url: suggestedPost.contentPerSocialNetwork.linkedin.pdfUrl }
        ]);
      }
      if (
        suggestedPost?.contentPerSocialNetwork?.instagram?.picturesUrls
          ?.length > 0
      ) {
        setImageInstagram(
          suggestedPost.contentPerSocialNetwork.instagram.picturesUrls.map(
            (url: string) => ({ url })
          )
        );
      } else if (suggestedPost?.contentPerSocialNetwork?.instagram?.videoUrl) {
        setImageInstagram([
          { url: suggestedPost.contentPerSocialNetwork.instagram.videoUrl }
        ]);
      }
      if (
        suggestedPost?.contentPerSocialNetwork?.whatsapp?.picturesUrls?.length >
        0
      ) {
        setImageWhatsapp(
          suggestedPost.contentPerSocialNetwork.whatsapp.picturesUrls.map(
            (url: string) => ({ url })
          )
        );
      } else if (suggestedPost?.contentPerSocialNetwork?.whatsapp?.videoUrl) {
        setImageWhatsapp([
          { url: suggestedPost.contentPerSocialNetwork.whatsapp.videoUrl }
        ]);
      }
      if (suggestedPost?.contentPerSocialNetwork?.tiktok?.videoUrl) {
        setImageTiktok([
          { url: suggestedPost.contentPerSocialNetwork.tiktok.videoUrl }
        ]);
      }
    }
    setLoader(false);
  };

  const handleTypeFile = (body: any) => {
    setType(body);
  };

  const handleImagen = (select: any, content: string) => {
    // Normalize to array - handles both single object from UploadFileVideo and arrays from other sources
    const normalizedSelect = Array.isArray(select)
      ? select
      : select
      ? [select]
      : [];
    switch (content) {
      // case 'photo':
      //   setImage(normalizedSelect);
      //   break;
      case 'twitter':
        setImageTwitter(normalizedSelect);
        break;
      case 'linkedin':
        setImageLinkedin(normalizedSelect);
        break;
      case 'instagram':
        setImageInstagram(normalizedSelect);
        break;
      case 'whatsapp':
        setImageWhatsapp(normalizedSelect);
        break;
      case 'tiktok':
        setImageTiktok(normalizedSelect);
        break;
    }
  };

  const regextValidationTwitter = (content: string) => {
    const regex = new RegExp(no_characters);
    return regex.test(content);
  };

  const regextValidationLinkedin = (content: string) => {
    return true;
  };
  const regextValidationTiktok = (content: string) => {
    if (!content || content.trim() === '') return true;
    const regex = new RegExp(no_mentions);
    return regex.test(content);
  };

  const onSubmit = async (suggestedPostData: any) => {
    setFormSubmitted(true);
    setLoader(true);
    if (
      !isDraft.current &&
      !suggestedPostData.includesFacebook &&
      !suggestedPostData.includesTwitter &&
      !suggestedPostData.includesLinkedin &&
      !suggestedPostData.includesInstagram &&
      !suggestedPostData.includesWhatsapp &&
      !suggestedPostData.includesTiktok
    ) {
      setErrorSocialNetwork(t('error_social_network'));
      setBackendError(t('error_social_network'));
      setLoader(false);
      return;
    }

    if (!isDraft.current && !suggestedPostData.description) {
      setBackendError(t('required_field'));
      setLoader(false);
      return;
    }

    if (!isDraft.current) {
      if (suggestedPostData.includesTwitter) {
        const validationContent = regextValidationTwitter(
          suggestedPostData.contentTwitter
        );
        const contentTwitterLength = suggestedPostData.contentTwitter
          ? TwitterText.getTweetLength(suggestedPostData.contentTwitter)
          : 0;
        if (!suggestedPostData.contentTwitter) {
          setBackendError(t('error_text_twitter'));
          setLoader(false);
          return;
        } else if (!validationContent) {
          setBackendError(t('characters'));
          setLoader(false);
          return;
        } else if (contentTwitterLength > contentLimits.twitter) {
          setBackendError(t('error_limit'));
          setInvalidLimit({
            ...invalidLimit,
            twitter: true
          });
          setLoader(false);
          return;
        }
      }

      if (suggestedPostData.includesLinkedin) {
        const validationContent = regextValidationLinkedin(
          suggestedPostData.contentLinkedin
        );
        const urlPattern = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g;
        const countPattern = (
          (suggestedPostData.contentLinkedin || '').match(urlPattern) || []
        ).length;
        const contentLinkedinLength = suggestedPostData.contentLinkedin
          ? suggestedPostData.contentLinkedin
              .replace(/(?:https?|ftp):\/\/\S+/g, ' ')
              .trim().length + countPattern
          : 0;
        if (
          (type.linkedin === 'share' || type.linkedin === 'text') &&
          !suggestedPostData.contentLinkedin
        ) {
          setBackendError(t('error_text_linkedin'));
          setLoader(false);
          return;
        } else if (!validationContent) {
          setBackendError(t('pattern'));
          setLoader(false);
          return;
        } else if (contentLinkedinLength > contentLimits.linkedin) {
          setBackendError(t('error_limit'));
          setInvalidLimit({
            ...invalidLimit,
            linkedin: true
          });
          setLoader(false);
          return;
        }
      }

      if (
        suggestedPostData.includesFacebook &&
        !facebookURLs(suggestedPostData.linkFacebook)
      ) {
        setBackendError(t('error_url'));
        setLoader(false);
        return;
      }

      if (suggestedPostData.includesInstagram) {
        const urlPattern = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-`][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g;
        const countPattern = (
          (suggestedPostData.contentInstagram || '').match(urlPattern) || []
        ).length;
        const contentInstagramLength = suggestedPostData.contentInstagram
          ? suggestedPostData.contentInstagram
              .replace(/(?:https?|ftp):\/\/\S+/g, ' ')
              .trim().length + countPattern
          : 0;
        if (contentInstagramLength > contentLimits.instagram) {
          setBackendError(t('error_limit'));
          setInvalidLimit({
            ...invalidLimit,
            instagram: true
          });
          setLoader(false);
          return;
        }
      }
      if (suggestedPostData.includesTiktok) {
        const validationContent = regextValidationTiktok(
          suggestedPostData.contentTiktok
        );
        const urlPattern = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g;
        const countPattern = (
          (suggestedPostData.contentTiktok || '').match(urlPattern) || []
        ).length;
        const contentTiktokLength = suggestedPostData.contentTiktok
          ? suggestedPostData.contentTiktok
              .replace(/(?:https?|ftp):\/\/\S+/g, ' ')
              .trim().length + countPattern
          : 0;
        if (!validationContent) {
          setBackendError(t('pattern_tiktok'));
          setLoader(false);
          return;
        }
        if (contentTiktokLength > contentLimits.tiktok) {
          setBackendError(t('error_limit'));
          setInvalidLimit({
            ...invalidLimit,
            tiktok: true
          });
          setLoader(false);
          return;
        }
      }

      if (suggestedPostData.includesWhatsapp) {
        const urlPattern = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g;
        const countPattern = (
          (suggestedPostData.contentWhatsapp || '').match(urlPattern) || []
        ).length;
        const contentWhatsappLength = suggestedPostData.contentWhatsapp
          ? suggestedPostData.contentWhatsapp
              .replace(/(?:https?|ftp):\/\/\S+/g, ' ')
              .trim().length + countPattern
          : 0;
        if (contentWhatsappLength > contentLimits.whatsapp) {
          setBackendError(t('error_limit'));
          setInvalidLimit({
            ...invalidLimit,
            whatsapp: true
          });
          setLoader(false);
          return;
        }
      }
    }

    suggestedPostData.companyId = currentUser.user.companyId;
    suggestedPostData.ambassadorId = currentUser.user.id;
    suggestedPostData.status = isDraft.current ? 'draft' : 'pending';

    // if (image) {
    //   const namePhoto = generateRandAlphaNumStr();
    //   const uploadedPhoto = await awsUploadFileHelper(
    //     currentUser?.token,
    //     image,
    //     true,
    //     'CAMPAIGN',
    //     namePhoto,
    //     t,
    //     setBackendError
    //   );
    //   if (uploadedPhoto) {
    //     suggestedPostData.photo = getUrlS3('campaigns', namePhoto);
    //   } else {
    //     setBackendError(t('validations:file_error'));
    //     setLoader(false);
    //     return;
    //   }
    // }

    let instagram: any;
    let whatsapp: any;
    let tiktok: any;

    if (suggestedPostData.includesInstagram) {
      if (type.instagram === 'image') {
        if (!imageInstagram[0]?.file?.type) {
          const name = imageInstagram[0].url.split('/').reverse()[0];
          instagram = {
            type: type['instagram'],
            url: [getUrlS3('campaigns', name)]
          };
        } else {
          const nameInstagram = setFileNameHelper(imageInstagram[0], false);
          const uploadedInstagramPhoto = await awsUploadFileHelper(
            currentUser?.token,
            imageInstagram[0],
            false,
            'CAMPAIGN',
            nameInstagram,
            t,
            setBackendError
          );
          if (uploadedInstagramPhoto) {
            instagram = {
              type: type['instagram'],
              url: [getUrlS3('campaigns', nameInstagram)]
            };
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
      if (type.instagram === 'video') {
        const instagramFileArg = Array.isArray(imageInstagram)
          ? imageInstagram[0]
          : imageInstagram;
        if (
          instagramFileArg &&
          !instagramFileArg?.file?.type &&
          (typeof instagramFileArg === 'string' || instagramFileArg?.url)
        ) {
          const url =
            typeof instagramFileArg === 'string'
              ? instagramFileArg
              : instagramFileArg.url;
          const name = url.split('/').reverse()[0];
          instagram = {
            type: type['instagram'],
            url: getUrlS3('campaigns', name)
          };
        } else {
          setUploadedInstagram(false);
          const nameInstagram = setFileNameHelper(instagramFileArg, false);
          const uploadedInstagram = await awsUploadVideoHelper(
            currentUser?.token,
            instagramFileArg,
            'CAMPAIGN',
            nameInstagram,
            t,
            setBackendError
          );
          if (uploadedInstagram) {
            instagram = {
              type: type['instagram'],
              url: getUrlS3('campaigns', nameInstagram)
            };
            setUploadedInstagram(true);
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
    }
    if (suggestedPostData.includesTiktok) {
      if (type.tiktok === 'video') {
        const tiktokFileArg = Array.isArray(imageTiktok)
          ? imageTiktok[0]
          : imageTiktok;
        if (
          tiktokFileArg &&
          !tiktokFileArg?.file?.type &&
          (typeof tiktokFileArg === 'string' || tiktokFileArg?.url)
        ) {
          const url =
            typeof tiktokFileArg === 'string'
              ? tiktokFileArg
              : tiktokFileArg.url;
          const name = url.split('/').reverse()[0];
          tiktok = {
            type: type['tiktok'],
            url: getUrlS3('campaigns', name)
          };
        } else if (tiktokFileArg) {
          setUploadedTiktok(false);
          const nameTiktok = setFileNameHelper(tiktokFileArg, false);
          const uploadedTiktok = await awsUploadVideoHelper(
            currentUser?.token,
            tiktokFileArg,
            'CAMPAIGN',
            nameTiktok,
            t,
            setBackendError
          );
          if (uploadedTiktok) {
            tiktok = {
              type: type['tiktok'],
              url: getUrlS3('campaigns', nameTiktok)
            };
            setUploadedTiktok(true);
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
    }

    if (suggestedPostData.includesWhatsapp) {
      if (type.whatsapp === 'image') {
        if (!imageWhatsapp[0]?.file?.type) {
          const name = imageWhatsapp[0].url.split('/').reverse()[0];
          whatsapp = {
            type: type['whatsapp'],
            url: [getUrlS3('campaigns', name)]
          };
        } else {
          const nameWhatsapp = setFileNameHelper(imageWhatsapp[0], false);
          const uploadedWhatsappPhoto = await awsUploadFileHelper(
            currentUser?.token,
            imageWhatsapp[0],
            false,
            'CAMPAIGN',
            nameWhatsapp,
            t,
            setBackendError
          );
          if (uploadedWhatsappPhoto) {
            whatsapp = {
              type: type['whatsapp'],
              url: [getUrlS3('campaigns', nameWhatsapp)]
            };
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
      if (type.whatsapp === 'video') {
        const whatsappFileArg = Array.isArray(imageWhatsapp)
          ? imageWhatsapp[0]
          : imageWhatsapp;
        if (
          whatsappFileArg &&
          !whatsappFileArg?.file?.type &&
          (typeof whatsappFileArg === 'string' || whatsappFileArg?.url)
        ) {
          const url =
            typeof whatsappFileArg === 'string'
              ? whatsappFileArg
              : whatsappFileArg.url;
          const name = url.split('/').reverse()[0];
          whatsapp = {
            type: type['whatsapp'],
            url: getUrlS3('campaigns', name)
          };
        } else {
          setUploadedWhatsapp(false);
          const nameWhatsapp = setFileNameHelper(whatsappFileArg, false);
          const uploadedWhatsapp = await awsUploadVideoHelper(
            currentUser?.token,
            whatsappFileArg,
            'CAMPAIGN',
            nameWhatsapp,
            t,
            setBackendError
          );
          if (uploadedWhatsapp) {
            whatsapp = {
              type: type['whatsapp'],
              url: getUrlS3('campaigns', nameWhatsapp)
            };
            setUploadedWhatsapp(true);
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
    }

    let twitter: any;
    let linkedin: any;

    if (suggestedPostData.includesTwitter) {
      if (type.twitter === 'image') {
        const twitterFile = [];
        if (imageTwitter) {
          setUploadedTwitter(false);
        }
        for (const file of imageTwitter) {
          if (!file?.file?.type) {
            const name = file.url.split('/').reverse()[0];
            twitterFile.push({ name });
            continue;
          }
          const nameTwitter = setFileNameHelper(file, false);
          await awsUploadFileHelper(
            currentUser?.token,
            file,
            false,
            'CAMPAIGN',
            nameTwitter,
            t,
            setBackendError
          );
          twitterFile.push({ name: nameTwitter });
        }
        twitter = {
          type: type['twitter'],
          url: twitterFile.map((file: any) => getUrlS3('campaigns', file?.name))
        };
      }
      if (type.twitter === 'video') {
        const twitterFileArg = Array.isArray(imageTwitter)
          ? imageTwitter[0]
          : imageTwitter;
        if (
          twitterFileArg &&
          !twitterFileArg?.file?.type &&
          (typeof twitterFileArg === 'string' || twitterFileArg?.url)
        ) {
          const url =
            typeof twitterFileArg === 'string'
              ? twitterFileArg
              : twitterFileArg.url;
          const name = url.split('/').reverse()[0];
          twitter = {
            type: type['twitter'],
            url: getUrlS3('campaigns', name)
          };
        } else {
          setUploadedTwitter(false);
          const nameTwitter = setFileNameHelper(twitterFileArg, false);
          const uploadedTwitter = await awsUploadVideoHelper(
            currentUser?.token,
            twitterFileArg,
            'CAMPAIGN',
            nameTwitter,
            t,
            setBackendError
          );
          if (uploadedTwitter) {
            twitter = {
              type: type['twitter'],
              url: getUrlS3('campaigns', nameTwitter)
            };
            setUploadedTwitter(true);
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
    }

    if (suggestedPostData.includesLinkedin) {
      if (type.linkedin === 'image') {
        const linkedinFile = [];
        if (imageLinkedin) {
          setUploadedLinkedin(false);
        }
        for (const file of imageLinkedin) {
          if (!file?.file?.type) {
            const name = file.url.split('/').reverse()[0];
            linkedinFile.push({ name });
            continue;
          }
          const nameLinkedin = setFileNameHelper(file, false);
          await awsUploadFileHelper(
            currentUser?.token,
            file,
            false,
            'CAMPAIGN',
            nameLinkedin,
            t,
            setBackendError
          );
          linkedinFile.push({ name: nameLinkedin });
        }
        linkedin = {
          type: type['linkedin'],
          url: linkedinFile.map((file: any) =>
            getUrlS3('campaigns', file?.name)
          )
        };
      }
      if (type.linkedin === 'video') {
        const linkedinFileArg = Array.isArray(imageLinkedin)
          ? imageLinkedin[0]
          : imageLinkedin;
        if (
          linkedinFileArg &&
          !linkedinFileArg?.file?.type &&
          (typeof linkedinFileArg === 'string' || linkedinFileArg?.url)
        ) {
          const url =
            typeof linkedinFileArg === 'string'
              ? linkedinFileArg
              : linkedinFileArg.url;
          const name = url.split('/').reverse()[0];
          linkedin = {
            type: type['linkedin'],
            url: getUrlS3('campaigns', name)
          };
        } else {
          setUploadedLinkedin(false);
          const nameLinkedin = setFileNameHelper(linkedinFileArg, false);
          const uploadedLinkedin = await awsUploadVideoHelper(
            currentUser?.token,
            linkedinFileArg,
            'CAMPAIGN',
            nameLinkedin,
            t,
            setBackendError
          );
          if (uploadedLinkedin) {
            linkedin = {
              type: type['linkedin'],
              url: getUrlS3('campaigns', nameLinkedin)
            };
            setUploadedLinkedin(true);
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
      if (type.linkedin === 'pdf') {
        const linkedinFileArg = Array.isArray(imageLinkedin)
          ? imageLinkedin[0]
          : imageLinkedin;
        if (
          linkedinFileArg &&
          !linkedinFileArg?.file?.type &&
          (typeof linkedinFileArg === 'string' || linkedinFileArg?.url)
        ) {
          const url =
            typeof linkedinFileArg === 'string'
              ? linkedinFileArg
              : linkedinFileArg.url;
          const name = url.split('/').reverse()[0];
          linkedin = {
            type: type['linkedin'],
            url: getUrlS3('campaigns', name)
          };
        } else {
          const nameLinkedin = setFileNameHelper(imageLinkedin[0], false);
          const uploadedLinkedin = await awsUploadFileHelper(
            currentUser?.token,
            imageLinkedin[0],
            false,
            'CAMPAIGN',
            nameLinkedin,
            t,
            setBackendError
          );
          if (uploadedLinkedin) {
            linkedin = {
              type: type['linkedin'],
              url: getUrlS3('campaigns', nameLinkedin)
            };
          } else {
            setBackendError(t('validations:file_error'));
            setLoader(false);
            return;
          }
        }
      }
    }

    let photo: string | undefined;

    // Logic to select the first available image
    if (
      twitter &&
      twitter.type === 'image' &&
      twitter.url &&
      twitter.url.length > 0
    ) {
      photo = twitter.url[0];
    } else if (
      linkedin &&
      linkedin.type === 'image' &&
      linkedin.url &&
      linkedin.url.length > 0
    ) {
      photo = linkedin.url[0];
    } else if (
      instagram &&
      instagram.type === 'image' &&
      instagram.url &&
      instagram.url.length > 0
    ) {
      photo = instagram.url[0];
    } else if (
      whatsapp &&
      whatsapp.type === 'image' &&
      whatsapp.url &&
      whatsapp.url.length > 0
    ) {
      photo = whatsapp.url[0];
    }

    const { data, errors } = id
      ? await updateSuggestedPost(
          currentUser?.token,
          id,
          suggestedPostData,
          twitter,
          linkedin,
          instagram,
          tiktok,
          whatsapp,
          type,
          photo
        )
      : await createSuggestedPost(
          currentUser?.token,
          suggestedPostData,
          twitter,
          linkedin,
          instagram,
          tiktok,
          whatsapp,
          type,
          photo
        );

    if (data) {
      Swal.fire({
        title: isDraft.current
          ? t('suggested_post:confirmation_draft_title')
          : t('suggested_post:confirmation_save_title'),
        text: isDraft.current
          ? t('suggested_post:confirmation_draft_message')
          : t('suggested_post:confirmation_save_message'),
        icon: 'success',
        confirmButtonText: t('suggested_post:close')
      }).then(() => {
        history.push('/suggested-posts');
      });
      setLoader(false);
      return;
    }

    if (errors) {
      setBackendError(
        errors?.data || errors?.message || t('validations:file_error')
      );
      setLoader(false);
      return;
    }

    setLoader(false);
  };

  return (
    <>
      {backendSuccess && (
        <SuccessAlert
          text={backendSuccess}
          allowClose={true}
          onClose={() => setBackendSuccess(null)}
        />
      )}

      {backendError && (
        <ErrorAlert
          text={backendError}
          allowClose={true}
          onClose={() => setBackendError(null)}
        />
      )}

      <CreateSuggestedPostForm
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        errors={errors}
        setValue={setValue}
        handleImage={handleImagen}
        control={control}
        handleTypeFile={handleTypeFile}
        errorSocialNetwork={errorSocialNetwork}
        fileErrorInstagram={fileErrorInstagram}
        fileErrorTwitter={fileErrorTwitter}
        fileErrorLinkedin={fileErrorLinkedin}
        fileErrorWhatsapp={fileErrorWhatsapp}
        fileErrorTiktok={fileErrorTiktok}
        loader={loader}
        contentLimits={contentLimits}
        invalidLimit={invalidLimit}
        uploadedTwitter={uploadedTwitter}
        uploadedLinkedin={uploadedLinkedin}
        uploadedInstagram={uploadedInstagram}
        uploadedTiktok={uploadedTiktok}
        uploadedWhatsapp={uploadedWhatsapp}
        watch={watch}
        setIsDraft={(val: boolean) => {
          isDraft.current = val;
          setLocalIsDraft(val);
        }}
        localIsDraft={localIsDraft}
        imageTwitter={imageTwitter}
        imageLinkedin={imageLinkedin}
        imageInstagram={imageInstagram}
        imageTiktok={imageTiktok}
        imageWhatsapp={imageWhatsapp}
        type={type}
        setType={setType}
        isEditing={!!id}
        initialSocialNetworks={initialSocialNetworks}
        formSubmitted={formSubmitted}
      />
    </>
  );
};

export default CreateSuggestedPostPage;
